/**
 * The "secondary" dashboard prefetch caches — pure post-await `setQueryData`
 * writes (strategy 2 in lib/prefetch/entries/dashboard.ts's module doc): the
 * integration list, onboarding-tagged proposals, and feature flags. Split out
 * of dashboardEntry.execute purely to keep that function's size down — no
 * behavior change from inlining these blocks directly.
 *
 * Import jobs used to be seeded here too; they gate the whole page's loading
 * state, so they moved to a pre-await registration in dashboard.ts (strategy 1).
 */
import type { QueryClient } from '@tanstack/react-query';

import type { CrmObjectListSentinelPage } from '@/lib/crm/store';
import { crmObjectListSentinelQuery } from '@/lib/crm/store';
import type { ProposalListItem } from '@/lib/proposals/core/types';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
import type { AssociationFilter } from '@/types/crm';

import { prefetchDebug } from '../debug';
import type { DashboardQuickfetchResponse } from './dashboardQuickfetchTypes';

/**
 * The integration-list query useMyIntegrationEntityIds runs for `userEntityId`.
 * Built via the SAME crmObjectListSentinelQuery call the hook uses, to
 * guarantee a hash-identical key. Shared with the parse-time hydration step.
 */
export function integrationsListQueryFor(orgId: number, userEntityId: string) {
  return crmObjectListSentinelQuery(orgId, 'ahoy_external_integration', {
    filter_groups: [
      {
        filters: [
          {
            association: 'ahoy_integration_to_owner',
            operator: 'exact',
            value: userEntityId,
          } as AssociationFilter,
        ],
      },
    ],
  });
}

/** Seed the integration-list cache from a known id set. */
export function seedIntegrationsListCache(
  queryClient: QueryClient,
  orgId: number,
  userEntityId: string,
  integrationEntityIds: string[]
): void {
  const sortedIds = [...integrationEntityIds].sort();
  queryClient.setQueryData<CrmObjectListSentinelPage>(
    integrationsListQueryFor(orgId, userEntityId).queryKey,
    { ids: sortedIds, next: null, count: sortedIds.length }
  );
}

export function seedSecondaryDashboardCaches(
  response: DashboardQuickfetchResponse,
  queryClient: QueryClient,
  orgId: number
): void {
  // Seed the integration-list cache that useMyIntegrationEntityIds reads, so the
  // meetings query is reachable on first render without a dependent round-trip.
  // Skip when user_entity_id is null (hook's query is disabled then) or
  // integration_entity_ids is absent.
  if (
    response.user_entity_id != null &&
    Array.isArray(response.integration_entity_ids)
  ) {
    prefetchDebug('seeding integrations list cache', {
      integrationCount: response.integration_entity_ids.length,
    });
    seedIntegrationsListCache(
      queryClient,
      orgId,
      response.user_entity_id,
      response.integration_entity_ids
    );
  }

  // Seed the onboarding-actions list cache that DashboardHome reads via
  // `useMyPendingProposalsPaginated(orgId, 1, 50, 'onboarding')`. Those
  // proposals are already present in the prefetched main list (each item
  // carries its `filter_tags`), but under a different cache key — so without
  // this seed the onboarding query cold-fetches on mount and the AI Actions
  // section renders the proposals as plain rows, then re-renders them into
  // the colorful onboarding treatment a beat later. Filtering the data we
  // already have removes that flash with no extra request. The key mirrors
  // the hook's params exactly (undefined fields are omitted when hashed, so
  // we match `filter_tags__contains: 'onboarding'`, page 1, page_size 50).
  if (Array.isArray(response.proposals?.results)) {
    const onboardingResults = (
      response.proposals.results as ProposalListItem[]
    ).filter(p => p.filter_tags?.includes('onboarding'));
    queryClient.setQueryData(
      proposalQueryKeys.list(orgId, {
        resolution: 'pending',
        assigned_to_me: true,
        page: 1,
        page_size: 50,
        filter_tags__contains: 'onboarding',
      }),
      {
        count: onboardingResults.length,
        next: null,
        previous: null,
        results: onboardingResults,
      }
    );
  }

  // Seed the feature-flags cache that useFeatureFlags reads (query key
  // ['featureFlags', orgId]). Strategy 2 (post-await) is correct here —
  // components that gate rendering on flags mount after initial render,
  // not during the in-flight window. When the backend field is null
  // (evaluation failed), skip the seed so useFeatureFlags falls back to
  // its standalone GET — graceful degradation.
  if (response.feature_flags !== null && response.feature_flags !== undefined) {
    prefetchDebug('seeding feature flags cache', {
      flagCount: response.feature_flags.length,
    });
    queryClient.setQueryData<string[]>(
      ['featureFlags', orgId],
      response.feature_flags
    );
  }
}
